import { Key } from 'react';
import { CustomSensorModel, GetTagListMultipleSensorsDataModel, GetTagListSingleSensorDataModel, MultipleItemListModel } from './models';
import { SensorEntityModel } from 'core/domain/sensors/models/sensorEntityModels';
import { mapTagsToLabels } from 'utils/sensorTags';
import { UpdateSensorTagListDataModel } from 'core/domain/sensors/models';

export const MAX_SIZE_RESPONSIVE_SCREEN = 600;

export const defaultSensor: CustomSensorModel = {
  id: '',
  uid: '',
  name: '',
  sensorType: '',
  room: '',
  usage: '',
  areValuesAccumulated: false,
  canDataBeFetched: true,
  deviceId: '',
  gatewayId: null,
  sensorId: '',
  availableTags: [],
  tags: [],
  parsedAvailableTags: [],
  isEditingTagList: false,
  isRowSelected: false,
  disabledRowSelection: false,
  page: 0,
};

export const parseTags = (value: string | string[] | null): string[] => {
  if (Array.isArray(value)) {
    return value;
  }

  return value ? [value] : [];
};

export const setParsedSensorList = ({
  data,
  selectedSensors,
  currentPage,
}: {
  data: SensorEntityModel[];
  selectedSensors?: CustomSensorModel[];
  currentPage: number;
}): CustomSensorModel[] => {
  return data.map((currentSensor) => {
    const selectedTags: string[] = [];
    const filteredTags = !!selectedSensors?.length
      ? selectedSensors.flatMap(({ availableTags }) => availableTags.filter((tag) => !selectedTags.includes(tag)))
      : [];
    const parsedAvailableTags = mapTagsToLabels(filteredTags);
    const isRowSelected = !!selectedSensors?.find(({ id }) => id === currentSensor.id);

    return {
      ...currentSensor,
      parsedAvailableTags,
      isEditingTagList: false,
      isRowSelected,
      disabledRowSelection: false,
      page: currentPage,
    };
  });
};

export const getFilteredMultipleTagEditorSelectedRows = (selectedRows: MultipleItemListModel[]) => {
  return selectedRows.filter((row) => !row.isParent).map(({ key }) => key as Key);
};

export const getTagListSingleSensorData = ({ tags, sensor }: GetTagListSingleSensorDataModel): UpdateSensorTagListDataModel[] => {
  const { deviceId, gatewayId, sensorId } = sensor;

  return [{ deviceId, gatewayId, sensorId, tags }];
};

export const getTagListMultipleSensorsData = ({ sensors, tags }: GetTagListMultipleSensorsDataModel): UpdateSensorTagListDataModel[] => {
  return sensors
    .filter((sensor) => sensor.isRowSelected)
    .map(({ deviceId, gatewayId, sensorId }) => ({ deviceId, gatewayId, sensorId, tags }));
};

export const transformTags = (tags: Key[]): { key: string; sensorId: string }[] => {
  return tags.map((tag) => {
    const [key, sensorId] = (tag as string).split(':');
    return { key, sensorId };
  });
};

export const setCurrentPageParsedSensorListToBeSent = (sensors: CustomSensorModel[]): SensorEntityModel[] => {
  return sensors.map((sensor) => sensor);
};

export const updateCurrentPageSensorList = ({
  sensors,
  tags,
}: {
  sensors: CustomSensorModel[];
  tags: { key: string; sensorId: string }[];
}) => {
  return sensors.map((sensor) => {
    const updatedTags: string[] = tags.filter((tag) => tag.sensorId === sensor.id).map(({ key }) => key);

    return !!updatedTags.length ? { ...sensor, tags: updatedTags } : { ...sensor, tags: [] };
  });
};

export const getParsedSensorsToBeUpdated = ({
  sensors,
  currentPageSensors,
  tags,
}: {
  sensors: SensorEntityModel[];
  currentPageSensors: CustomSensorModel[];
  tags: Key[];
}): SensorEntityModel[] => {
  const transformedTags = transformTags(tags);
  const updatedCurrentPageSensors = updateCurrentPageSensorList({ sensors: currentPageSensors, tags: transformedTags });
  const typedCurrentPageSensors = setCurrentPageParsedSensorListToBeSent(updatedCurrentPageSensors);

  return sensors.map((sensor) => {
    const currentPageSensor = typedCurrentPageSensors.find((currentSensor) => currentSensor.id === sensor.id);

    return currentPageSensor || sensor;
  });
};
